
import { ApiError } from '@eneco/dc/client/ApiError';
import { getAddressPublic } from '@eneco/dc/services/AddressPublicService';
import {
  Customers_Address_AddressModel,
  ResponseModels_Customers_Address_AddressModel,
  DC_Repositories_Base_Enumerations_BusinessUnit,
  DC_Repositories_Base_Enumerations_Label,
} from '@monorepo-types/dc';
import type { EventObject } from 'xstate';
import { assign, raise, setup, fromPromise } from 'xstate';

interface MachineConfig {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
}

interface Values {
  postalCode: string;
  houseNumber: number;
}

export interface AddressMachineContext extends Omit<Customers_Address_AddressModel, 'houseNumber'> {
  errorMessage?: string;
  houseNumber?: number;
  lastCallData?: {
    cachedResponse?: ResponseModels_Customers_Address_AddressModel;
    eventValues: Values;
  };
}

export interface AddressMachineEvent extends EventObject {
  type: 'GET_ADDRESS' | 'NOT_FOUND';
  config: MachineConfig;
  values: Values;
}

const getService = async (
  config: MachineConfig,
  values: Values,
): Promise<ResponseModels_Customers_Address_AddressModel> => {
  try {
    const response = await getAddressPublic({
      ...config,
      postalCode: values.postalCode,
      houseNumber: values.houseNumber.toString(),
      houseNumberSuffix: '',
    });

    // Filter out null values from houseNumberSuffixes
    if (response?.data?.houseNumberSuffixes) {
      response.data.houseNumberSuffixes = response.data.houseNumberSuffixes.filter(Boolean);
    } else if (response?.data) {
      response.data.houseNumberSuffixes = [];
    }

    return response;
  } catch (error) {
    throw new Error((error as ApiError)?.message);
  }
};

export const addressMachine = setup({
  types: {
    context: {} as AddressMachineContext,
    events: {} as AddressMachineEvent,
    output: {} as ResponseModels_Customers_Address_AddressModel,
  },
  actors: {
    fetchAddress: fromPromise<
      ResponseModels_Customers_Address_AddressModel,
      { config: MachineConfig; values: Values; lastCallData?: Exclude<AddressMachineContext['lastCallData'], undefined> }
    >(async ({ input }) => {
      const { config, values, lastCallData } = input;
      values.postalCode = values.postalCode?.replace(/\s/g, '');

      if (
        Object.keys(values).every(key => values[key as keyof Values] === lastCallData?.eventValues[key as keyof Values])
      ) {
        return { ...lastCallData?.cachedResponse };
      }

      return await getService(config, values);
    }),
  },
  actions: {
    clearErrorMessage: assign({ errorMessage: undefined }),
    clearContext: assign({
      street: undefined,
      postalCode: undefined,
      houseNumber: undefined,
      houseNumberSuffix: undefined,
      houseNumberSuffixes: undefined,
      city: undefined,
      bus: undefined,
      lastCallData: undefined,
    }),
  },
}).createMachine({
  id: 'address',
  initial: 'IDLE',
  context: {},
  on: {
    GET_ADDRESS: { target: '.FETCHING' },
  },
  states: {
    IDLE: {},
    FETCHING: {
      invoke: {
        src: 'fetchAddress',
        input: ({ context, event }) => ({
          config: event.config,
          values: event.values,
          lastCallData: context.lastCallData,
        }),
        onDone: {
          target: 'SUCCESS',
          actions: assign(({ event }) => {
            const data = event.output?.data || {};
            return {
              ...data,
              houseNumber: Number(data.houseNumber || 0),
              postalCode: data.postalCode?.replace(/\s/g, '') || '',
              lastCallData: {
                eventValues: {
                  postalCode: data.postalCode?.replace(/\s/g, '') || '',
                  houseNumber: Number(data.houseNumber || 0),
                },
                cachedResponse: event.output,
              },
            };
          }),
        },
        onError: {
          target: 'ERROR',
          actions: [
            ({ event }) =>
              raise({
                type: event.error instanceof Error && event.error.message === 'Not Found' ? 'NOT_FOUND' : '',
              }),
            assign({
              errorMessage: ({ event }) => (event.error instanceof Error ? event.error.message : 'Unknown error'),
            }),
          ],
        },
      },
    },
    SUCCESS: {
      entry: ['clearErrorMessage'],
    },
    ERROR: {
      entry: ['clearContext'],
      on: {
        NOT_FOUND: { target: 'NOT_FOUND' },
      },
    },
    NOT_FOUND: {},
  },
});
